import type {
  CapacityPoint,
  CapacityState,
  InterfaceTraffic,
  StatusTransition,
  TrafficQuantity,
} from '@/hooks/useDeviceTrafficSeries';
import type { Incident } from '@/hooks/useIncidents';
import type { InterfaceSummary } from '@/hooks/useDeviceTrafficSummary';

/**
 * Inbound packets per second below which the non-unicast ratio means nothing.
 * Mirrors the backend's own floor: a quiet access port is nearly 100%
 * non-unicast by packet count because there is no unicast to dilute the ARP.
 */
export const STORM_FLOOR_PPS = 100;

/**
 * One row per timestamp, carrying every quantity the surface draws.
 *
 * One row set feeds every block on the surface, which is what lets them share a
 * recharts `syncId` and one crosshair — the reader can then *see* that a
 * discard burst sits on the traffic knee instead of eyeballing two charts.
 *
 * Outbound values are stored **negated**. Direction rides the sign channel:
 * inbound above the zero line, outbound below, on one signed axis whose ticks
 * print the magnitude. That is the MRTG form every network engineer has read
 * for twenty years, and it frees hue entirely for identity.
 */
export interface TrafficRow {
  ts: number;
  bitsIn: number | null;
  bitsOut: number | null;
  errorsIn: number | null;
  errorsOut: number | null;
  discardsIn: number | null;
  discardsOut: number | null;
  /** Non-unicast as a percentage of all inbound packets. */
  nonUnicastShare: number | null;
  /** Total inbound packets per second, for the floor the share is judged on. */
  inboundPps: number | null;
  /** Line rate, and its mirror, drawn only where a denominator resolved. */
  capacity: number | null;
  capacityMirror: number | null;
  utilizationIn: number | null;
  utilizationOut: number | null;
}

function seriesMap(iface: InterfaceTraffic, quantity: TrafficQuantity): Map<number, number> {
  const points = iface.series.find((s) => s.quantity === quantity)?.points ?? [];
  const out = new Map<number, number>();
  for (const [ts, value] of points) {
    // A null is dropped rather than stored as zero: the store is saying it does
    // not know what happened in this bucket, which is not "nothing happened".
    if (value !== null) out.set(ts, value);
  }
  return out;
}

/** Whether the endpoint returned this quantity at all, gaps included. */
export function hasQuantity(iface: InterfaceTraffic, quantity: TrafficQuantity): boolean {
  return iface.series.some((s) => s.quantity === quantity && s.points.length > 0);
}

/**
 * Every timestamp any quantity reported, ascending.
 *
 * The union rather than the first series' own timestamps: the quantities are
 * fetched as separate queries over identical buckets, and a bucket one of them
 * has nothing for is a hole in that series, not a row that should not exist.
 */
function timeline(iface: InterfaceTraffic): number[] {
  const stamps = new Set<number>();
  for (const s of iface.series) for (const [ts] of s.points) stamps.add(ts);
  for (const c of iface.capacity) stamps.add(c.ts);
  return [...stamps].sort((a, b) => a - b);
}

export function buildRows(iface: InterfaceTraffic): TrafficRow[] {
  const bitsIn = seriesMap(iface, 'bits_in');
  const bitsOut = seriesMap(iface, 'bits_out');
  const errorsIn = seriesMap(iface, 'errors_in');
  const errorsOut = seriesMap(iface, 'errors_out');
  const discardsIn = seriesMap(iface, 'discards_in');
  const discardsOut = seriesMap(iface, 'discards_out');
  const packetsIn = seriesMap(iface, 'packets_in');
  const nonUnicastIn = seriesMap(iface, 'nonunicast_in');
  const capacity = new Map(iface.capacity.map((c) => [c.ts, c]));

  return timeline(iface).map((ts) => {
    const unicast = packetsIn.get(ts);
    const nonUnicast = nonUnicastIn.get(ts);
    const inboundPps =
      unicast === undefined && nonUnicast === undefined ? null : (unicast ?? 0) + (nonUnicast ?? 0);
    // Both operands or neither: a share computed from one of them would be
    // either 100% or 0% purely because the other query had a hole here.
    const share =
      unicast !== undefined && nonUnicast !== undefined && unicast + nonUnicast > 0
        ? (nonUnicast / (unicast + nonUnicast)) * 100
        : null;

    const bits = capacity.get(ts)?.bits ?? null;
    const inbound = bitsIn.get(ts) ?? null;
    const outbound = bitsOut.get(ts) ?? null;
    const utilizationOut = percentOf(outbound, bits);

    return {
      ts,
      bitsIn: inbound,
      bitsOut: outbound === null ? null : -outbound,
      errorsIn: errorsIn.get(ts) ?? null,
      errorsOut: negate(errorsOut.get(ts)),
      discardsIn: discardsIn.get(ts) ?? null,
      discardsOut: negate(discardsOut.get(ts)),
      nonUnicastShare: share,
      inboundPps,
      capacity: bits,
      capacityMirror: bits === null ? null : -bits,
      utilizationIn: percentOf(inbound, bits),
      utilizationOut: utilizationOut === null ? null : -utilizationOut,
    };
  });
}

function negate(value: number | undefined): number | null {
  return value === undefined ? null : -value;
}

function percentOf(value: number | null, capacityBits: number | null): number | null {
  if (value === null || capacityBits === null || capacityBits <= 0) return null;
  return (value / capacityBits) * 100;
}

/** What the denominator situation is across the whole window. */
export interface CapacitySummary {
  /** The state that held for the most points. */
  dominant: CapacityState;
  /** Line rate where one resolved at all, taken from the last `known` point. */
  bits: number | null;
  counts: Record<CapacityState, number>;
  /** Whether any point at all carries a usable denominator. */
  anyKnown: boolean;
}

export function summariseCapacity(capacity: CapacityPoint[]): CapacitySummary {
  const counts: Record<CapacityState, number> = {
    known: 0,
    link_down: 0,
    no_physical_rate: 0,
    unknown: 0,
  };
  let bits: number | null = null;
  for (const point of capacity) {
    counts[point.state] += 1;
    if (point.state === 'known' && point.bits !== null) bits = point.bits;
  }
  const dominant = (Object.keys(counts) as CapacityState[]).reduce((best, state) =>
    counts[state] > counts[best] ? state : best,
  );
  return { dominant, bits, counts, anyKnown: counts.known > 0 };
}

/** One stretch of time the link spent in a single state. */
export interface StatusSpan {
  from: number;
  to: number;
  state: string;
  /** True when the span's start is the window's edge rather than an observed change. */
  openStart: boolean;
}

/**
 * The window carved into spans by the observed transitions.
 *
 * A rail of dots says a port bounced; spans say how long it was down, which is
 * the question a reader actually arrives with. The leading span is marked
 * `openStart` because it began before the window and we are only showing the
 * part we can see.
 *
 * Returns nothing at all when the state entering the window is unknown *and*
 * nothing changed inside it — that is an absence, and drawing a span for it
 * would assert a state nobody observed.
 */
export function statusSpans(
  statusBefore: string | null,
  transitions: StatusTransition[],
  start: number,
  end: number,
): StatusSpan[] {
  const spans: StatusSpan[] = [];
  let state = statusBefore;
  let since = start;
  let openStart = true;

  for (const transition of transitions) {
    if (state !== null) spans.push({ from: since, to: transition.ts, state, openStart });
    state = transition.to;
    since = transition.ts;
    openStart = false;
  }
  if (state !== null) spans.push({ from: since, to: end, state, openStart });
  return spans;
}

/** One incident, as a band on the event rail. */
export interface IncidentBand {
  id: string;
  title: string;
  /** When it really opened and closed. An open incident ends at the window. */
  from: number;
  to: number;
  /** The same span clipped to the window, which is what actually gets drawn. */
  drawFrom: number;
  drawTo: number;
  open: boolean;
  /** True when the band starts at the window edge rather than at its own start. */
  clipped: boolean;
}

/**
 * The incidents overlapping a window, clipped to it.
 *
 * The clipping is the point. Recharts' `ifOverflow="hidden"` does not clip a
 * reference area whose edge falls outside the domain — it drops the area
 * entirely, so an incident that opened before the window drew *nothing*, while
 * still being listed in the text beneath. Most incidents worth looking at
 * opened before the window you are looking at.
 *
 * The true bounds are kept alongside the drawn ones so the text can say when it
 * really started, and `clipped` lets the reader be told that the band's left
 * edge is the window's edge and not the incident's.
 */
export function incidentBands(incidents: Incident[], start: number, end: number): IncidentBand[] {
  return incidents
    .map((incident) => {
      const from = new Date(incident.createdAt).getTime();
      const open = incident.resolvedAt === null;
      const to = open ? end : new Date(incident.resolvedAt ?? '').getTime();
      return {
        id: incident.id,
        title: incident.title,
        from,
        to,
        drawFrom: Math.max(from, start),
        drawTo: Math.min(to, end),
        open,
        clipped: from < start,
      };
    })
    .filter((b) => Number.isFinite(b.from) && Number.isFinite(b.to) && b.from <= end && b.to >= start)
    .sort((a, b) => a.from - b.from);
}

/** Frames this port lost, either way, for either reason. */
export function faults(row: InterfaceSummary): number {
  return (row.errorsIn ?? 0) + (row.errorsOut ?? 0) + (row.discardsIn ?? 0) + (row.discardsOut ?? 0);
}

/**
 * Rows that are ports on the front of the device, as opposed to rows that are
 * something else the device also calls an interface.
 *
 * Two fields, because neither is enough alone. `interfaceType` does most of the
 * work — it removes aggregates, VLAN interfaces, tunnels and loopbacks — but
 * Junos types four internal pseudo-interfaces as `ethernetCsmacd`, and
 * `connectorPresent` is what excludes those. The `!== false` rather than
 * `=== true` is load-bearing in the other direction: one vendor does not serve
 * the column at all, and requiring a positive answer would empty its list.
 *
 * An empty cage stays in the list. It is a port with nothing plugged into it,
 * not a row that stopped being a port, and the front of the device looks the
 * same either way — `operStatus === 'notPresent'` is what draws it as empty.
 */
export function isFrontPanelPort(row: InterfaceSummary): boolean {
  return row.role !== 'unit' && row.interfaceType === 'ethernetCsmacd' && row.connectorPresent !== false;
}

/** How to call a port: by its name, which every row has (gateway#102). */
export function portName(row: { interfaceName: string }): string {
  return row.interfaceName;
}
